<script setup lang="ts">
import { computed, h, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { AdminRecord } from '@/types/record'
import type { BatchActionDef, FormFieldDef, ListQuery, PageDefinition, RowActionDef, SelectOption } from '@/types/page'
import { useAuthStore } from '@/stores/auth'
import { usePrototypeStore } from '@/stores/prototype'
import { useUiStore } from '@/stores/ui'
import { useTaskStore } from '@/stores/tasks'
import { useWorkspaceStore } from '@/stores/workspace'
import { adapter, type ActionOutcome } from '@/services/prototype-adapter'
import { filterRecords } from '@/services/query'
import { request } from '@/services/request'
import { usePageTable } from '@/composables/usePageTable'
import { avatarText, avatarTone, cellTone, displayCellValue, fieldLabel, formatFieldValue, progressValue } from '@/utils/presentation'
import { formatDate, formatDateTime, formatMoney, formatNumber, formatPercent } from '@/utils/format'
import { downloadBlob, rowsToCsv } from '@/utils/export'
import { nowIso } from '@/utils/format'
import AppIcon from '@/components/AppIcon.vue'
import ProSummary from './ProSummary.vue'
import ProFilters from './ProFilters.vue'
import ProPagination from './ProPagination.vue'
import ProStatus from './ProStatus.vue'
import ProDetailDrawer from './ProDetailDrawer.vue'
import ProFormDrawer from './ProFormDrawer.vue'
import ActionDialog from './ActionDialog.vue'
import ColumnSettingsDialog from './ColumnSettingsDialog.vue'
import ImportDialog from './ImportDialog.vue'
import MasterDataDrawer from './MasterDataDrawer.vue'
import RolePermissionsDialog from './RolePermissionsDialog.vue'
import { masterForPanel, type MasterConfig } from '@/data/masters'

const props = withDefaults(defineProps<{
  definition: PageDefinition
  embedded?: boolean
}>(), { embedded: false })

const auth = useAuthStore()
const ui = useUiStore()
const store = usePrototypeStore()
const tasks = useTaskStore()
const workspace = useWorkspaceStore()
const router = useRouter()
const route = useRoute()
const table = usePageTable(() => props.definition)

const detailOpen = ref(false)
const detailRow = ref<AdminRecord | null>(null)
const formOpen = ref(false)
const formMode = ref<'create' | 'edit'>('create')
const formRow = ref<AdminRecord | null>(null)
const formPreset = ref<Record<string, unknown> | null>(null)
const actionOpen = ref(false)
const actionLoading = ref(false)
const batchBusy = ref(false)
const actionTitle = ref('')
const actionDescription = ref('')
const actionFields = ref<FormFieldDef[]>([])
const actionConfirmText = ref('确认')
const actionTone = ref<'primary' | 'danger' | 'default'>('primary')
const pendingAction = ref<RowActionDef | null>(null)
const pendingRow = ref<AdminRecord | null>(null)
const pendingBatch = ref<BatchActionDef | null>(null)
const pendingBatchRows = ref<AdminRecord[]>([])
const columnOpen = ref(false)
const hiddenKeys = ref<string[]>(hiddenColumnsFor(props.definition))
const importOpen = ref(false)
const activePanel = ref('')
const masterOpen = ref(false)
const masterConfig = ref<MasterConfig | null>(null)
const roleDialogOpen = ref(false)
const roleDialogRecord = ref<AdminRecord | null>(null)

function hiddenColumnsFor(page: PageDefinition): string[] {
  const saved = workspace.columns[page.id]
  if (saved?.length) return page.columns.filter(column => !saved.includes(column.key)).map(column => column.key)
  return page.columns.filter(column => column.hideByDefault).map(column => column.key)
}

const canCreate = computed(() => Boolean(props.definition.createLabel && props.definition.createPermission && auth.has(props.definition.createPermission) && !props.definition.readOnly))
const canEdit = computed(() => Boolean(props.definition.editPermission && auth.has(props.definition.editPermission) && !props.definition.readOnly))
const canImport = computed(() => Boolean(props.definition.importPermission && auth.has(props.definition.importPermission)))
const canExport = computed(() => Boolean(props.definition.exportPermission && auth.has(props.definition.exportPermission) && props.definition.exportScope !== 'none'))
const selectable = computed(() => !props.definition.hideSelection && Boolean(props.definition.batchActions?.length))

const visibleColumns = computed(() => props.definition.columns.filter(column => !hiddenKeys.value.includes(column.key) && (!column.hideBelow || ui.viewportWidth >= column.hideBelow)))
const tableMinWidth = computed(() => {
  const total = visibleColumns.value.reduce((sum, column) => sum + (column.width ?? column.minWidth ?? 120), 0)
  return Math.max(760, total + (selectable.value ? 42 : 0) + 132)
})
const extraActions = computed(() => (props.definition.extraActions ?? []).filter(item => auth.has(item.permission)))
const directExtras = computed(() => extraActions.value.filter(item => !item.overflowOnly).slice(0, 2))
const moreExtras = computed(() => extraActions.value.filter(item => item.overflowOnly || !directExtras.value.includes(item)))
const allSelected = computed(() => table.rows.value.length > 0 && table.selectedKeys.value.length === table.rows.value.length)
const someSelected = computed(() => table.selectedKeys.value.length > 0 && !allSelected.value)
const selectedRows = computed(() => table.rows.value.filter(row => table.selectedKeys.value.includes(String(row.id))))
const batchActions = computed(() => (props.definition.batchActions ?? []).filter(item => auth.has(item.permission)))
const hasMoreForRow = (row: AdminRecord) => moreRowActions(row).length > 0

function hasPermission(permission?: string): boolean {
  return auth.has(permission)
}

function rowVisible(action: RowActionDef, row: AdminRecord): boolean {
  if (!hasPermission(action.permission)) return false
  if (!action.visibleWhen) return true
  return action.visibleWhen.in.some(value => String(value) === String(row[action.visibleWhen?.field ?? ''] ?? ''))
}

function availableRowActions(row: AdminRecord): RowActionDef[] {
  return (props.definition.rowActions ?? []).filter(action => rowVisible(action, row))
}

function preferredActions(row: AdminRecord): RowActionDef[] {
  const available = availableRowActions(row)
  const priority = ['view', 'edit']
  const selected: RowActionDef[] = []
  for (const key of priority) {
    const action = available.find(item => item.key === key)
    if (action) selected.push(action)
  }
  for (const action of available) {
    if (selected.length >= 2) break
    if (!selected.some(item => item.key === action.key)) selected.push(action)
  }
  return selected
}

function moreRowActions(row: AdminRecord): RowActionDef[] {
  const preferred = preferredActions(row)
  return availableRowActions(row).filter(action => !preferred.some(item => item === action))
}

function toggleAll(checked: boolean): void {
  table.selectedKeys.value = checked ? table.rows.value.map(row => String(row.id)) : []
}

function toggleRow(row: AdminRecord, checked: boolean): void {
  const id = String(row.id)
  table.selectedKeys.value = checked
    ? [...new Set([...table.selectedKeys.value, id])]
    : table.selectedKeys.value.filter(item => item !== id)
}

function statusLabel(row: AdminRecord, key: string, options?: SelectOption[]): string {
  const raw = row[key]
  return props.definition.statusMap?.[String(raw)]?.label
    ?? options?.find(item => String(item.value) === String(raw))?.label
    ?? (typeof raw === 'boolean' ? (raw ? '是' : '否') : String(raw ?? '—'))
}

function statusToneFor(row: AdminRecord, key: string, options?: SelectOption[]): string {
  const raw = String(row[key] ?? '')
  return props.definition.statusMap?.[raw]?.tone
    ?? options?.find(item => String(item.value) === raw)?.tone
    ?? cellTone(row, props.definition.columns.find(column => column.key === key) ?? { key, label: key }, props.definition)
}

function avatarClass(row: AdminRecord): string {
  return avatarTone(row.name ?? row.user_name ?? row.generic_name ?? row.title)
}

function displayValue(row: AdminRecord, column: PageDefinition['columns'][number]): string {
  if (column.key === 'stock') {
    const unit = String(row.unit ?? '')
    return `${formatNumber(row.stock)} ${unit}`
  }
  return displayCellValue(row, column, props.definition)
}

function primaryName(row: AdminRecord): string {
  const key = props.definition.columns.find(column => column.type === 'primary')?.key ?? 'name'
  return String(row[key] ?? row.name ?? row.title ?? row.generic_name ?? '—')
}

function subtitle(row: AdminRecord): string {
  const key = props.definition.columns.find(column => column.type === 'primary')?.subtitleKey
  return key ? String(row[key] ?? '') : ''
}

function formatCell(row: AdminRecord, key: string): string {
  return formatFieldValue(row[key], props.definition, key)
}

function openDetail(row: AdminRecord): void {
  detailRow.value = row
  detailOpen.value = true
}

function openCreate(): void {
  formMode.value = 'create'
  formRow.value = null
  formPreset.value = null
  formOpen.value = true
}

/** Prefill a create form from a parent record without mutating the source row. */
function openCreateWith(preset: Record<string, unknown>): void {
  formMode.value = 'create'
  formRow.value = null
  formPreset.value = { ...preset }
  formOpen.value = true
}

function openEdit(row: AdminRecord): void {
  formMode.value = 'edit'
  formRow.value = { ...row }
  formOpen.value = true
}

function onSaved(record?: AdminRecord): void {
  if (props.definition.id === 'health-reports') {
    tasks.createTask({ taskType: '生成健康报告', title: `生成 ${record?.user_name ?? '用户'} 健康报告`, total: 1 })
  }
  table.afterMutation()
}

function customFields(action: RowActionDef, row: AdminRecord): FormFieldDef[] {
  if (props.definition.id === 'health-alerts' && action.key === 'assign') {
    const specialty: Record<string, string> = { 血压: '心血管内科', 血糖: '内分泌科', 心率: '心血管内科', 血氧: '呼吸内科', 体温: '老年医学', 体重: '临床营养' }
    const target = specialty[String(row.alert_type ?? '')]
    const all = adapter.options('care_team')
    const matched = target ? all.filter(option => option.label.includes(target)) : all
    return [{ key: 'owner_id', label: '跟进人', type: 'select', required: true, options: matched.length ? matched : all, help: target ? `优先展示${target}专长的启用成员` : '仅展示启用中的医护成员' }]
  }
  if (action.form?.length) return action.form
  if (props.definition.id === 'users' && action.key === 'tags') {
    return [
      { key: 'health_tags', label: '健康标签', type: 'multiselect', optionSource: 'user_tags', required: false, help: '标签会影响预警规则适用人群与消息受众' },
      { key: 'note', label: '调整说明', type: 'textarea', full: true, maxLength: 120 },
    ]
  }
  if (props.definition.id === 'users' && action.key === 'bind-device') {
    return [{ key: 'device_id', label: '可绑定设备', type: 'select', optionSource: 'devices', required: true, help: '仅展示未绑定用户的设备，绑定后设备开始接收健康数据' }]
  }
  if (props.definition.id === 'families' && action.key === 'add-member') {
    return [
      { key: 'user_id', label: '选择用户', type: 'select', optionSource: 'users', required: true },
      { key: 'family_role', label: '家庭角色', type: 'select', options: ['本人', '配偶', '子女', '父母', '其他亲属'].map(value => ({ value, label: value })), required: true },
    ]
  }
  if (props.definition.id === 'families' && action.key === 'remove-member') {
    const members = store.db.users.filter(user => String(user.family_id) === String(row.id))
    return [
      {
        key: 'user_id',
        label: '选择移除成员',
        type: 'select',
        required: true,
        options: members.map(member => ({ value: String(member.id), label: `${member.name} · ${member.family_role || '家庭成员'}` })),
        help: members.length ? '只列出当前家庭成员；移除最后一名守护人会被拒绝' : '当前家庭没有可移除成员',
      },
      { key: 'note', label: '移除原因', type: 'textarea', required: true, full: true },
    ]
  }
  if (props.definition.id === 'care-team' && action.key === 'assign-families') {
    return [{ key: 'family_ids', label: '负责家庭', type: 'multiselect', optionSource: 'families', required: true }]
  }
  if (props.definition.id === 'admin-users' && action.key === 'reset-password') {
    return [{ key: 'reason', label: '重置原因', type: 'textarea', required: true, full: true }]
  }
  if (props.definition.id === 'support-tickets' && action.key === 'handle') {
    return [{ key: 'resolution', label: '处理结果', type: 'textarea', required: true, full: true }]
  }
  return []
}

function handleRowAction(action: RowActionDef, row: AdminRecord): void {
  if (action.key === 'view' || action.key === 'preview') {
    openDetail(row)
    return
  }
  if (action.key === 'edit') {
    openEdit(row)
    return
  }
  if (action.key === 'add-child') {
    const nextLevel = row.level === 'province' ? 'city' : 'district'
    openCreateWith({ parent_id: String(row.id), level: nextLevel, status: 'active' })
    return
  }
  if (action.key === 'open-device') {
    const deviceName = String(row.device_name ?? '').trim()
    if (!deviceName) {
      fail('该条健康指标没有关联设备，无法查看设备状态')
      return
    }
    success(`正在定位设备 ${deviceName}`)
    void router.push({ path: '/devices', query: { keyword: deviceName } })
    return
  }
  if (action.key === 'permissions' && props.definition.id === 'roles') {
    roleDialogRecord.value = row
    roleDialogOpen.value = true
    return
  }
  if (action.key === 'timeline' || action.key === 'workload' || action.key === 'permissions') {
    openDetail(row)
    return
  }
  const fields = customFields(action, row)
  pendingAction.value = action
  pendingRow.value = row
  if (fields.length) {
    actionTitle.value = action.label
    actionDescription.value = action.confirmText || props.definition.notice || '确认后将立即写入当前原型内存，并记录到操作日志。'
    actionFields.value = fields
    actionConfirmText.value = action.label
    actionTone.value = action.tone === 'danger' ? 'danger' : 'primary'
    actionOpen.value = true
    return
  }
  if (action.confirm) {
    void confirmAndExecute(action, row)
    return
  }
  void executeAction(action, row)
}

async function confirmAndExecute(action: RowActionDef, row: AdminRecord, values: Record<string, unknown> = {}): Promise<void> {
  if (action.confirm) {
    try {
      await ElMessageBox.confirm(
        action.confirmText || `确定要${action.label}「${primaryName(row)}」吗？该操作会写入原型操作日志。`,
        action.confirmTitle || `确认${action.label}`,
        { type: 'warning', confirmButtonText: action.label, cancelButtonText: '取消', distinguishCancelAndClose: true },
      )
    } catch {
      return
    }
  }
  await executeAction(action, row, values)
}

function closeAction(): void {
  actionOpen.value = false
  pendingAction.value = null
  pendingRow.value = null
  pendingBatch.value = null
  pendingBatchRows.value = []
  actionFields.value = []
}

async function confirmAction(values: Record<string, unknown>): Promise<void> {
  actionLoading.value = true
  await new Promise(resolve => setTimeout(resolve, 240))
  if (pendingBatch.value) {
    const action = pendingBatch.value
    actionLoading.value = false
    closeAction()
    await runBatch(action, values)
    return
  }
  if (pendingAction.value && pendingRow.value) {
    const action = pendingAction.value
    const row = pendingRow.value
    actionLoading.value = false
    closeAction()
    await executeAction(action, row, values)
    return
  }
  actionLoading.value = false
  closeAction()
}

function success(message: string): void {
  ElMessage.success(message)
}

function fail(message: string): void {
  ElMessage.error(message)
}

/** Present adapter-provided rule tests, versions and member lists as a real
 * result surface instead of a toast that hides the evidence. */
function showOutcomeInfo(outcome: ActionOutcome): void {
  if (!outcome.info) return
  const content = h('div', { style: 'display:grid;gap:8px;white-space:normal;line-height:1.65' }, [
    ...outcome.info.lines.map(line => h('div', { style: 'display:grid;grid-template-columns:112px minmax(0,1fr);gap:10px' }, [
      h('span', { style: 'color:#7183a1' }, line.label),
      h('span', { style: 'color:#1f2d45;font-weight:600;word-break:break-word' }, line.value),
    ])),
    ...(outcome.info.note ? [h('p', { style: 'margin:4px 0 0;color:#9aa8bd;font-size:11px' }, outcome.info.note)] : []),
  ])
  void ElMessageBox.alert(content, outcome.info.title, {
    confirmButtonText: '知道了',
    customClass: 'outcome-message-box',
  }).catch(() => undefined)
}

async function executeAction(action: RowActionDef, row: AdminRecord, values: Record<string, unknown> = {}): Promise<void> {
  const id = row.id
  const def = props.definition
  const patch: Record<string, unknown> = {}

  // Adapter-backed operations carry the authoritative consistency rules
  // (reference conflicts, resource migration, rule history and derived
  // counters). Keeping them on this path means a visible action either
  // publishes an explicit outcome or a concrete conflict; it never falls
  // through to the generic "尚未绑定" branch.
  if (['disable-region', 'enable-region', 'migrate-region', 'risk-recompute', 'remove-member', 'tags', 'correct', 'remind', 'sync', 'firmware', 'copy', 'test-rule', 'version-history', 'members'].includes(action.key)) {
    try {
      const mapped = action.key === 'tags' ? { ...values, tags: values.health_tags } : values
      const outcome = await request(
        () => adapter.executeRowAction(def, String(id), action.key, mapped, auth.profile?.name ?? '管理员'),
        { write: true, delay: 260, useSimulation: true },
      )
      success(outcome.message)
      showOutcomeInfo(outcome)
      table.afterMutation()
    } catch (caught) {
      fail(caught instanceof Error ? caught.message : '操作失败，请重试')
    }
    return
  }

  // Every row action is a write. Run it through the same latency/failure
  // boundary as list queries so timeout, 401/403/409/422/429/500 and offline
  // never mutate the memory database before the operation is acknowledged.
  try {
    await request(() => true, { write: true, delay: 200, useSimulation: true })
  } catch (caught) {
    fail(caught instanceof Error ? caught.message : '操作未完成，请稍后重试')
    return
  }

  if (action.transition) {
    const result = store.transition(def, [id], action.transition.field, action.transition.value, String(values.reason ?? ''), action.transition.statusLabel)
    result.ok ? success(result.message) : fail(result.message)
    if (result.ok) table.afterMutation()
    return
  }

  if (action.key === 'delete') {
    if (def.id === 'links' && Number(row.usage_count ?? 0) > 0) {
      fail(`该链接仍被 ${row.usage_count} 处引用，请先解除引用`)
      return
    }
    const result = store.removeRecords(def, [id])
    result.ok ? success(result.message) : fail(result.message)
    if (result.ok) table.afterMutation()
    return
  }

  switch (action.key) {
    case 'status-toggle':
      patch.status = row.status === 'active' ? 'disabled' : 'active'
      if (values.reason) patch.reason = values.reason
      break
    case 'cancel-account': {
      const openOrders = store.db.orders.filter(order => String(order.user_id) === String(id) && ['pending', 'refund_pending'].includes(String(order.status)))
      const openTickets = store.db.tickets.filter(ticket => String(ticket.user_id) === String(id) && !['resolved', 'closed'].includes(String(ticket.status)))
      if (openOrders.length || openTickets.length) {
        fail(`该用户仍有 ${openOrders.length} 个未完成订单、${openTickets.length} 个在办工单，需处理完成后才能注销`)
        return
      }
      patch.status = 'cancelled'
      patch.note = values.reason ?? ''
      break
    }
    case 'bind-device': {
      const device = store.db.devices.find(item => String(item.id) === String(values.device_id))
      const user = store.db.users.find(item => String(item.id) === String(id))
      if (!device || !user) {
        fail('所选设备或用户不存在，请刷新后重试')
        return
      }
      if (device.user_id && String(device.user_id) !== String(user.id)) {
        fail('该设备已绑定其他用户，请先解绑后再操作')
        return
      }
      Object.assign(device, {
        user_id: user.id,
        user_name: user.name,
        family_id: user.family_id,
        family_name: user.family_name,
        region_id: user.region_id,
        region_name: user.region_name,
        status: 'online',
        bound_at: nowIso(),
        updated_at: nowIso(),
        row_version: Number(device.row_version ?? 1) + 1,
      })
      patch.device_bound = true
      patch.device_count = store.db.devices.filter(item => String(item.user_id) === String(user.id)).length
      store.addAudit(def, '绑定设备', `${user.name} · ${device.device_no}`)
      break
    }
    case 'tags':
      patch.tags = values.health_tags ?? []
      patch.note = values.note ?? row.note ?? ''
      break
    case 'add-member': {
      const user = store.db.users.find(item => String(item.id) === String(values.user_id))
      const family = store.db.families.find(item => String(item.id) === String(id))
      if (!user || !family) {
        fail('用户或家庭不存在')
        return
      }
      user.family_id = family.id
      user.family_name = family.name
      user.family_role = String(values.family_role ?? '其他亲属')
      family.member_count = store.db.users.filter(item => String(item.family_id) === String(family.id)).length
      store.addAudit(def, '新增成员', `${family.name} · ${user.name}`)
      success(`已将 ${user.name} 加入 ${family.name}`)
      table.afterMutation()
      return
    }
    case 'remove-member': {
      const family = store.db.families.find(item => String(item.id) === String(id))
      const members = store.db.users.filter(item => String(item.family_id) === String(id))
      if (!family || members.length <= 1) {
        fail('家庭至少需要保留一名成员，移除最后一名成员会被拒绝')
        return
      }
      const target = members[members.length - 1]
      if (target) {
        target.family_id = ''
        target.family_name = ''
        family.member_count = members.length - 1
        family.guardian_count = Math.max(1, Number(family.guardian_count ?? 1) - 1)
      }
      store.addAudit(def, '移除成员', family.name, '成功', String(values.note ?? ''))
      success(`已移除 1 名家庭成员，剩余 ${members.length - 1} 人`)
      table.afterMutation()
      return
    }
    case 'risk-recompute': {
      const open = store.db.alerts.filter(alert => String(alert.family_id) === String(row.id) && ['pending', 'processing'].includes(String(alert.status)))
      const high = open.some(alert => alert.severity === '高')
      patch.risk_level = high || open.length >= 3 ? '高风险' : open.length ? '中风险' : '低风险'
      store.addAudit(def, '重算风险', String(row.name), '成功', `未关闭预警 ${open.length} 条`)
      break
    }
    case 'migrate-region': {
      const region = store.db.regions.find(item => String(item.id) === String(values.region_id ?? values.target_region_id))
      if (!region) {
        fail('目标区域不存在或已停用')
        return
      }
      patch.region_id = region.id
      patch.region_name = region.name
      patch.region_path = region.path
      if (values.address) patch.address = values.address
      break
    }
    case 'disable-region': {
      if (Number(row.resource_count ?? 0) > 0) {
        fail(`该区域仍关联 ${row.resource_count} 项资源，请先完成资源迁移再停用`)
        store.addAudit(def, '停用', String(row.name), '失败', '区域存在关联资源，操作被拒绝')
        return
      }
      patch.status = 'disabled'
      break
    }
    case 'enable-region':
      patch.status = 'active'
      break
    case 'remind': {
      const task = tasks.createTask({ taskType: '用药提醒', title: `向 ${row.user_name} 发送用药提醒`, total: 1 })
      success(`提醒任务 ${task.taskNo} 已进入队列`)
      return
    }
    case 'pause':
      patch.status = 'paused'
      patch.pause_reason = values.reason ?? ''
      break
    case 'resume':
      patch.status = 'active'
      patch.pause_reason = ''
      break
    case 'create-alert': {
      const alert = {
        id: `alert-${Date.now()}`,
        alert_no: `YJ${String(store.db.alerts.length + 1).padStart(6, '0')}`,
        user_id: row.user_id,
        user_name: row.user_name,
        family_id: row.family_id ?? '',
        family_name: row.family_name,
        rule_name: `${row.metric_type}异常预警`,
        alert_type: row.metric_type,
        metric_value: row.value_display,
        reference_range: row.reference_range,
        severity: row.severity,
        status: 'pending',
        owner_id: '',
        owner_name: '',
        triggered_at: nowIso(),
        sla_due_at: nowIso(),
        closed_at: '',
        close_reason: '',
        created_at: nowIso(),
        updated_at: nowIso(),
        row_version: 1,
      }
      store.db.alerts.unshift(alert)
      store.addAudit(def, '生成预警', `${row.user_name} · ${row.metric_type}`)
      success('健康预警已生成，可在健康预警列表继续处理')
      table.afterMutation()
      return
    }
    case 'download': {
      const content = `康护健康 · ${row.report_type ?? '健康报告'}\n报告编号：${row.report_no}\n用户：${row.user_name}\n统计周期：${row.period_start} 至 ${row.period_end}\n状态：${row.status}\n\n本文件由管理后台原型生成，用于确认报告下载交互。`
      downloadBlob(`${row.report_no}-${row.user_name}.txt`, content, 'text/plain;charset=utf-8')
      success('报告文件已开始下载')
      return
    }
    case 'retry':
    case 'generate': {
      patch.status = 'generating'
      patch.failure_reason = ''
      tasks.createTask({ taskType: '生成健康报告', title: `${row.user_name} 健康报告`, total: 1 })
      success('报告已重新进入生成队列')
      break
    }
    case 'assign': {
      if (def.id === 'support-tickets') {
        patch.assignee_name = values.assignee_name ?? row.assignee_name
        patch.status = 'assigned'
        break
      }
      const owner = store.db.care_team.find(item => String(item.id) === String(values.owner_id))
      if (!owner) {
        fail('请选择启用中的医护成员')
        return
      }
      patch.owner_id = owner.id
      patch.owner_name = owner.name
      patch.status = 'processing'
      patch.assigned_at = nowIso()
      break
    }
    case 'contact-family': {
      patch.status = 'processing'
      patch.contact_note = String(values.note ?? '')
      break
    }
    case 'generate-report': {
      const report = {
        id: `report-${Date.now()}`,
        report_no: `BG${String(store.db.reports.length + 1).padStart(6, '0')}`,
        user_id: row.user_id,
        user_name: row.user_name,
        family_name: row.family_name,
        period_start: '2026-10-11',
        period_end: '2026-10-17',
        report_type: '周报',
        template_name: '家庭健康周报模板',
        status: 'generating',
        generated_at: '',
        sent_at: '',
        failure_reason: '',
        asset_name: '',
        created_at: nowIso(),
        updated_at: nowIso(),
        row_version: 1,
      }
      store.db.reports.unshift(report)
      tasks.createTask({ taskType: '生成健康报告', title: `${row.user_name} 健康报告`, total: 1 })
      store.addAudit(def, '生成报告', `${row.user_name} 健康报告`)
      success('健康报告生成任务已创建，可在健康报告页查看')
      return
    }
    case 'handle':
      patch.status = 'resolved'
      if (values.note || values.resolution) patch.close_reason = values.note ?? values.resolution
      break
    case 'close':
    case 'batch-close':
      patch.status = 'closed'
      patch.closed_at = nowIso()
      if (values.close_reason || values.reason) patch.close_reason = values.close_reason ?? values.reason
      break
    case 'test-rule': {
      const task = tasks.createTask({ taskType: '规则测试', title: `验证预警规则「${row.name}」`, total: 1 })
      success(`测试任务 ${task.taskNo} 已创建，将生成模拟样例`)
      return
    }
    case 'toggle-rule':
    case 'toggle-team':
    case 'toggle': {
      const disabling = row.status === 'active'
      if (disabling && props.definition.id === 'links' && Number(row.usage_count ?? 0) > 0) {
        fail(`该链接仍被 ${row.usage_count} 处引用，请先在引用位置解除后再停用`)
        store.addAudit(def, '停用', String(row.name), '失败', '链接存在引用，操作被拒绝')
        return
      }
      if (disabling && props.definition.id === 'roles' && row.is_builtin && row.code === 'super_admin') {
        fail('内置超级管理员角色不可停用或移除关键权限')
        store.addAudit(def, '停用', String(row.name), '失败', '内置超级管理员角色受保护')
        return
      }
      if (disabling && props.definition.id === 'admin-users' && Array.isArray(row.role_codes) && row.role_codes.includes('super_admin')) {
        const activeSupers = store.db.admins.filter(admin => Array.isArray(admin.role_codes) && admin.role_codes.includes('super_admin') && admin.status === 'active')
        if (activeSupers.length <= 1) {
          fail('最后一名超级管理员不可停用，请先授予其他管理员超级管理员角色')
          store.addAudit(def, '停用', String(row.name), '失败', '最后一名超级管理员受保护')
          return
        }
      }
      if (disabling && props.definition.id === 'care-team') {
        const openAlerts = store.db.alerts.filter(alert => String(alert.owner_id) === String(id) && ['pending', 'processing'].includes(String(alert.status))).length
        if (Number(row.family_count ?? 0) > 0 || openAlerts > 0) {
          fail(`该成员仍负责 ${row.family_count ?? 0} 户家庭、${openAlerts} 条未关闭预警，请先转交服务再停用`)
          store.addAudit(def, '停用', String(row.name), '失败', '医护成员仍有关联家庭或预警')
          return
        }
      }
      patch.status = disabling ? 'disabled' : 'active'
      break
    }
    case 'send': {
      patch.status = 'sent'
      patch.sent_at = nowIso()
      patch.delivered = Number(row.delivered ?? 0) || 1
      tasks.createTask({ taskType: '发送消息', title: `发送消息「${row.title}」`, total: 1 })
      break
    }
    case 'cancel':
      patch.status = 'cancelled'
      break
    case 'copy': {
      const { id: _id, created_at: _created, updated_at: _updated, row_version: _version, ...rest } = row
      void _id; void _created; void _updated; void _version
      const result = store.createRecord(def, { ...rest, name: `${row.name}（副本）`, title: `${row.title}（副本）`, status: 'draft', version: 1 })
      result.ok ? success(result.message) : fail(result.message)
      if (result.ok) table.afterMutation()
      return
    }
    case 'test-send':
      store.addAudit(def, '测试发送', String(row.name))
      success('测试消息已发送到当前管理员账号，请在消息通知中查看')
      return
    case 'online':
      patch.status = 'online'
      break
    case 'publish':
      patch.status = props.definition.id === 'content-faq' ? 'published' : 'online'
      break
    case 'offline':
      if (props.definition.id === 'links') {
        fail('停用仍被引用的链接会返回冲突，请先查看引用位置')
        return
      }
      patch.status = props.definition.id === 'content-faq' ? 'offline' : props.definition.id === 'drugs' || props.definition.id === 'drug-review' ? 'offline' : 'offline'
      break
    case 'submit-review':
      patch.review_status = 'pending'
      break
    case 'approve':
      patch.review_status = 'approved'
      patch.updated_at = nowIso()
      break
    case 'reject':
      patch.review_status = 'rejected'
      patch.review_reason = values.reason ?? ''
      break
    case 'adjust-stock':
      patch.stock = Number(values.stock ?? row.stock)
      patch.stock_reason = values.reason ?? ''
      break
    case 'bind': {
      const user = store.db.users.find(item => String(item.id) === String(values.user_id))
      if (!user) {
        fail('请选择有效用户')
        return
      }
      patch.user_id = user.id
      patch.user_name = user.name
      patch.family_id = user.family_id
      patch.family_name = user.family_name
      patch.region_id = user.region_id
      patch.region_name = user.region_name
      patch.status = 'online'
      patch.bound_at = nowIso()
      break
    }
    case 'unbind':
      if (!String(values.reason ?? '').trim()) {
        fail('解绑设备必须填写原因，原因会写入操作日志')
        return
      }
      patch.user_id = ''
      patch.user_name = ''
      patch.family_id = ''
      patch.family_name = ''
      patch.status = 'unbound'
      patch.bound_at = ''
      patch.unbind_reason = String(values.reason)
      break
    case 'sync':
    case 'firmware':
      if (row.status !== 'online') {
        fail(`设备当前为${def.statusMap?.[String(row.status)]?.label ?? String(row.status)}，离线或故障设备不能执行命令`)
        return
      }
      tasks.createTask({ taskType: action.key === 'firmware' ? '固件升级' : '设备同步', title: `${row.device_no} ${action.label}`, total: 1 })
      success(`${action.label}任务已下发，可在任务中心查看进度`)
      return
    case 'fault':
      patch.status = 'fault'
      patch.fault_note = values.fault_note ?? '后台标记故障'
      break
    case 'resolve':
      patch.status = 'online'
      patch.fault_note = ''
      break
    case 'assign-families':
      patch.family_count = Array.isArray(values.family_ids) ? values.family_ids.length : row.family_count
      break
    case 'transfer-team': {
      const targetId = String(values.target_id ?? '')
      const target = store.db.care_team.find(member => String(member.id) === targetId && member.status === 'active')
      if (!target || String(target.id) === String(row.id)) {
        fail('请选择另一位启用中的医护成员接收服务')
        return
      }
      let alertCount = 0
      let planCount = 0
      for (const alert of store.db.alerts) {
        if (String(alert.owner_id) === String(row.id) && ['pending', 'processing'].includes(String(alert.status))) {
          alert.owner_id = target.id
          alert.owner_name = target.name
          alertCount += 1
        }
      }
      for (const plan of store.db.medication_plans) {
        if (String(plan.owner_id) === String(row.id)) {
          plan.owner_id = target.id
          plan.owner_name = target.name
          planCount += 1
        }
      }
      target.family_count = Number(target.family_count ?? 0) + Number(row.family_count ?? 0)
      patch.family_count = 0
      store.addAudit(def, '转交服务', String(row.name), '成功', `转交 ${row.family_count ?? 0} 户家庭、${alertCount} 条预警、${planCount} 个计划至${target.name}；${String(values.note ?? '')}`)
      const transferred = store.updateRecord(def, id, patch)
      if (!transferred.ok) fail(transferred.message)
      else {
        success(`已将 ${row.family_count ?? 0} 户家庭、${alertCount} 条预警、${planCount} 个计划转交至${target.name}`)
        table.afterMutation()
      }
      return
    }
    case 'refund': {
      if (row.status === 'refunded') {
        fail('该订单已经退款，不能重复操作')
        return
      }
      patch.status = 'refunded'
      patch.refund_amount_fen = row.amount_fen
      patch.refund_reason = values.refund_reason ?? ''
      break
    }
    case 'create-ticket': {
      if (def.id === 'support-feedback') {
        const ticketId = `ticket-${Date.now()}`
        const ticket = {
          id: ticketId,
          ticket_no: `GD${String(store.db.tickets.length + 1).padStart(6, '0')}`,
          user_id: row.user_id,
          user_name: row.user_name,
          family_name: '',
          type: '账号问题',
          priority: '中',
          status: 'pending',
          assignee_name: '',
          title: `用户反馈 ${row.feedback_no}`,
          description: String(row.content ?? ''),
          channel: '用户反馈',
          sla_due_at: nowIso(),
          created_at: nowIso(),
          updated_at: nowIso(),
          conversation: [],
          row_version: 1,
        }
        store.db.tickets.unshift(ticket)
        patch.ticket_no = ticket.ticket_no
        patch.status = 'processing'
        store.addAudit(def, '转工单', ticket.ticket_no)
        success(`已转为客服工单 ${ticket.ticket_no}`)
        break
      }
      const ticket = {
        id: `ticket-${Date.now()}`,
        ticket_no: `GD${String(store.db.tickets.length + 1).padStart(6, '0')}`,
        user_id: row.user_id,
        user_name: row.user_name,
        family_name: row.family_name ?? '',
        type: '会员支付',
        priority: '中',
        status: 'pending',
        assignee_name: '',
        title: `会员订单 ${row.order_no} 售后`,
        description: '由会员订单转入的售后工单。',
        channel: '管理后台',
        sla_due_at: nowIso(),
        created_at: nowIso(),
        updated_at: nowIso(),
        conversation: [],
        row_version: 1,
      }
      store.db.tickets.unshift(ticket)
      store.addAudit(def, '关联工单', ticket.ticket_no)
      success(`已创建客服工单 ${ticket.ticket_no}`)
      return
    }
    case 'close-order':
      patch.status = 'closed'
      patch.closed_at = nowIso()
      break
    case 'archive':
      if (Number(row.usage_count ?? 0) > 0) {
        fail(`素材仍被 ${row.usage_count} 处引用，请先解除引用`)
        return
      }
      patch.status = 'archived'
      break
    case 'replace':
      store.addAudit(def, '替换素材', String(row.name))
      success('素材替换已完成，原引用关系保持不变')
      return
    case 'reset-password':
      store.addAudit(def, '重置密码', String(row.name))
      ElMessageBox.alert('已生成一次性临时密码，并要求该管理员下次登录时修改。原型环境不会展示真实凭据。', '密码已重置', { confirmButtonText: '知道了' }).catch(() => undefined)
      return
    case 'force-logout':
      store.addAudit(def, '强制下线', String(row.name))
      success(`已断开 ${row.name} 的当前会话`)
      return
    case 'assign-ticket': {
      patch.assignee_name = values.assignee_name ?? row.assignee_name
      patch.status = 'assigned'
      break
    }
    case 'reply':
      if (def.id === 'support-tickets') {
        patch.status = 'processing'
        patch.last_reply = values.reply ?? values.reply_content ?? ''
        patch.replied_at = nowIso()
      } else {
        patch.status = 'replied'
        patch.reply = values.reply ?? values.reply_content ?? ''
        patch.replied_at = nowIso()
      }
      break
    case 'priority':
      patch.priority = values.priority
      patch.priority_updated_at = nowIso()
      break
    case 'escalate':
      patch.priority = '紧急'
      patch.escalated_at = nowIso()
      if (row.status === 'pending') patch.status = 'assigned'
      break
    case 'close-feedback':
      patch.status = 'closed'
      patch.closed_at = nowIso()
      break
    default:
      fail('该操作尚未绑定可观察的原型状态变化，请通过对应业务入口处理')
      return
  }

  // Read-only lists still expose a narrow set of audited row actions (for
  // example order refund). The generic edit path stays blocked, so these
  // actions go through the adapter's explicit transition rules.
  if (def.readOnly) {
    try {
      const outcome = await request(
        () => adapter.executeRowAction(def, String(id), action.key, { ...values, ...patch }, auth.profile?.name ?? '管理员'),
        { write: true, delay: 320, useSimulation: true },
      )
      success(outcome.message)
      table.afterMutation()
    } catch (caught) {
      fail(caught instanceof Error ? caught.message : '操作失败，请重试')
    }
    return
  }

  const result = store.updateRecord(def, id, patch)
  if (!result.ok) {
    fail(result.message)
    return
  }
  success(`${action.label}成功`)
  table.afterMutation()
}

function handleBatch(action: BatchActionDef): void {
  if (!table.selectedKeys.value.length) {
    fail('请先选择需要操作的记录')
    return
  }
  if (action.kind === 'export') {
    void exportData('selected')
    return
  }
  if (action.key === 'batch-import') {
    importOpen.value = true
    return
  }
  if (action.form?.length) {
    pendingBatch.value = action
    pendingBatchRows.value = selectedRows.value
    actionTitle.value = action.label
    actionDescription.value = action.confirmText || `已选择 ${selectedRows.value.length} 条记录，确认后逐项执行并展示结果。`
    actionFields.value = action.form
    actionConfirmText.value = action.label
    actionTone.value = action.tone === 'danger' ? 'danger' : 'primary'
    actionOpen.value = true
    return
  }
  void runBatch(action, {})
}

async function runBatch(action: BatchActionDef, values: Record<string, unknown>): Promise<void> {
  if (batchBusy.value) return
  const rows = pendingBatchRows.value.length ? [...pendingBatchRows.value] : [...selectedRows.value]
  if (!rows.length) return
  if (action.confirm) {
    try {
      await ElMessageBox.confirm(
        action.confirmText || `确定对选中的 ${rows.length} 条记录执行「${action.label}」吗？`,
        `确认${action.label}`,
        { type: action.tone === 'danger' ? 'warning' : 'info', confirmButtonText: action.label, cancelButtonText: '取消' },
      )
    } catch {
      return
    }
  }
  const ids = rows.map(row => row.id)
  let clearSelection = false
  batchBusy.value = true
  try {
    if (action.kind === 'delete') {
      const result = store.removeRecords(props.definition, ids)
      result.ok ? success(result.message) : fail(result.message)
      clearSelection = result.ok
    } else if (action.kind === 'export') {
      await exportData('selected')
      clearSelection = true
    } else {
      // Batch transitions and task-backed actions go through the same memory
      // adapter as single-row actions. The list update happens only after the
      // simulated request succeeds; a 401/403/409/422/429/500/offline scenario
      // leaves every selected row untouched and keeps the selection for retry.
      try {
        const backup = rows.map(row => ({ id: String(row.id), data: JSON.parse(JSON.stringify(row)) as AdminRecord }))
        const outcome = await request(
          () => adapter.executeBatch(props.definition, rows, action.key, values, auth.profile?.name ?? '管理员'),
          { write: true, delay: 340, useSimulation: true },
        )
        if (!outcome.ok) {
          // A partial batch must not leave a half-applied prototype state.
          // Restore every selected row and let the user retry the whole set.
          const source = adapter.table(adapter.moduleFor(props.definition))
          for (const item of backup) {
            const target = source.find(row => String(row.id) === item.id)
            if (target) Object.assign(target, item.data)
          }
          adapter.refreshDerived()
        }
        if (outcome.ok) {
          if (action.kind === 'task') {
            const task = tasks.createTask({
              taskType: action.label,
              title: `${props.definition.title} · ${action.label}`,
              total: rows.length,
            })
            success(`${outcome.message}，任务 ${task.taskNo} 已进入任务中心`)
          } else {
            success(outcome.message)
          }
          clearSelection = true
        } else {
          fail(outcome.message)
        }
      } catch (caught) {
        fail(caught instanceof Error ? caught.message : '批量操作未完成，请重试')
      }
    }
  } finally {
    if (clearSelection) table.selectedKeys.value = []
    batchBusy.value = false
    table.afterMutation()
  }
}

async function exportData(scope: 'current' | 'filtered' | 'selected' = 'current'): Promise<void> {
  const def = props.definition
  let rows: AdminRecord[]
  if (scope === 'selected') rows = selectedRows.value
  else if (scope === 'filtered') {
    // Build the filtered result set without the list's server pagination
    // cap. Passing pageSize=100000 to the adapter would be normalised back
    // to 20 and silently export only the first page.
    rows = filterRecords(adapter.all(def), def, {
      ...table.queryObject(),
      page: 1,
      pageSize: 100,
    } as ListQuery)
  } else rows = [...table.rows.value]
  if (!rows.length) {
    fail('当前范围没有可导出的数据')
    return
  }
  const columns = visibleColumns.value
  const csv = rowsToCsv(rows, columns, props.definition.statusMap)
  const stamp = formatDate(new Date()).replace(/\//g, '-')
  downloadBlob(`${def.title}-${stamp}.csv`, csv)
  tasks.createTask({ taskType: '导出', title: `${def.title} · ${scope === 'selected' ? '选中记录' : scope === 'filtered' ? '当前筛选' : '当前页'}`, total: rows.length })
  store.addAudit(def, '导出', `${rows.length} 条${def.title}`, '成功', '按当前角色和筛选范围导出')
  success(`已导出 ${rows.length} 条${def.title}，文件已开始下载`)
}

async function handleImport(rows: Array<Record<string, unknown>>): Promise<void> {
  if (!rows.length) return
  let successCount = 0
  try {
    await request(
      () => {
        for (const row of rows) {
          if (store.createRecord(props.definition, row).ok) successCount += 1
        }
        return successCount
      },
      { write: true, delay: 420, useSimulation: true },
    )
    if (!successCount) {
      fail('没有可导入的有效数据')
      return
    }
    tasks.createTask({ taskType: '批量导入', title: `${props.definition.title}批量导入`, total: rows.length, success: successCount })
    success(`成功导入 ${successCount} 行，已进入任务中心`)
    table.selectedKeys.value = []
    table.afterMutation()
  } catch (caught) {
    fail(caught instanceof Error ? caught.message : '导入未完成，请重试')
  }
}

const exportScopeLabel = computed(() => props.definition.exportScope === 'filtered' ? '导出当前筛选' : '导出当前页')

function exportDefault(): void {
  void exportData(props.definition.exportScope === 'filtered' ? 'filtered' : 'current')
}

function handleExtra(item: { key: string; kind?: string; to?: string; label: string }): void {
  if (item.kind === 'link' && item.to) {
    void router.push(item.to.replace(/^#/, ''))
    return
  }
  if (item.kind === 'import') {
    importOpen.value = true
    return
  }
  if (item.kind === 'export') {
    void exportDefault()
    return
  }
  if (item.key === 'scan') {
    success('已打开扫码录入，原型支持手动输入条形码继续新增药品')
    openCreate()
    return
  }
  if (item.key === 'sync-permissions') {
    const result = adapter.syncPermissionCatalog(auth.profile?.name ?? '管理员')
    success(`权限目录已同步：${result.permissions} 项权限，覆盖 ${result.modules} 个模块`)
    return
  }
  fail(`「${item.label}」尚未绑定可执行的原型交互，请在对应业务页面完成操作`)
}

function handleMoreCommand(command: RowActionDef | string, row: AdminRecord): void {
  if (typeof command === 'object' && command) {
    handleRowAction(command, row)
    return
  }
  if (command === 'detail') openDetail(row)
  if (command === 'edit') openEdit(row)
}

function handleMoreExtra(key: string): void {
  const item = extraActions.value.find(action => action.key === key)
  if (item) handleExtra(item)
}

function handleTableMore(command: string): void {
  if (command === 'refresh') table.refresh()
  else if (command === 'columns') columnOpen.value = true
  else if (command === 'reset') resetFilters()
  else if (command === 'export') exportDefault()
}

function updateColumnSettings(keys: string[]): void {
  hiddenKeys.value = keys
  workspace.saveColumns(props.definition.id, props.definition.columns.filter(column => !keys.includes(column.key)).map(column => column.key))
}

function applyFilters(next: Record<string, unknown>): void {
  for (const key of Object.keys(table.filters)) delete table.filters[key]
  Object.assign(table.filters, next)
}

function resetFilters(): void {
  table.reset()
}

function handlePanel(): void {
  const panel = String(route.query.panel ?? '')
  activePanel.value = panel
  if (!panel) return
  if (panel === 'create') {
    if (canCreate.value) openCreate()
    clearPanelQuery()
    return
  }
  const config = masterForPanel(props.definition.id, panel)
  if (config) {
    const permissionByPage: Record<string, string> = {
      users: 'users.tag',
      drugs: 'drugs.category',
      devices: 'devices.manage',
      'membership-plans': 'membership.plan',
      links: 'links.manage',
      'content-faq': 'content.manage',
      'health-reports': 'reports.template',
    }
    if (!auth.has(permissionByPage[config.pageId] ?? 'settings.manage')) {
      fail('当前角色没有维护该主数据的权限')
      return
    }
    masterConfig.value = config
    masterOpen.value = true
    clearPanelQuery()
    return
  }
}

function clearPanelQuery(): void {
  if (!route.query.panel) return
  const query = Object.fromEntries(Object.entries(route.query).filter(([key]) => key !== 'panel'))
  void router.replace({ path: route.path, query })
}

function applyQueryFilters(): boolean {
  let changed = false
  for (const filter of props.definition.filters) {
    const raw = route.query[filter.key]
    if (raw === undefined) continue
    let next: unknown
    if (filter.type === 'daterange') {
      if (typeof raw === 'string') {
        const parts = raw.split(',')
        next = [parts[0] ?? '', parts[1] ?? '']
      } else if (Array.isArray(raw)) {
        next = [String(raw[0] ?? ''), String(raw[1] ?? '')]
      } else {
        continue
      }
    } else if (Array.isArray(raw)) {
      next = raw.map(String)
    } else {
      next = String(raw)
    }
    if (JSON.stringify(table.filters[filter.key]) === JSON.stringify(next)) continue
    table.filters[filter.key] = next
    changed = true
  }
  return changed
}

function openFocusedRow(): void {
  const focus = typeof route.query.focus === 'string' ? route.query.focus : ''
  if (!focus) return
  const row = table.rows.value.find(item => String(item.name ?? item.user_name ?? item.title ?? '').includes(focus))
  if (row) openDetail(row)
}

function applyRouteQuery(): void {
  const keyword = typeof route.query.keyword === 'string' ? route.query.keyword : ''
  let changed = false
  if (keyword && table.keyword.value !== keyword) {
    table.keyword.value = keyword
    changed = true
  }
  if (applyQueryFilters()) changed = true
  if (changed) {
    table.page.value = 1
    void table.load('initial').then(() => window.setTimeout(openFocusedRow, 60))
  } else {
    window.setTimeout(openFocusedRow, 120)
  }
}

watch(() => route.query.panel, () => {
  if (!props.embedded) handlePanel()
}, { immediate: true })

watch(() => route.query, () => {
  if (!props.embedded) applyRouteQuery()
}, { deep: true })

watch(() => table.status.value, status => {
  if (status === 'success') openFocusedRow()
})

onMounted(() => {
  if (props.embedded) return
  window.setTimeout(() => applyRouteQuery(), 280)
})

defineExpose({ openCreate, openEdit, openDetail, exportData, handleExtra })

watch(() => props.definition.id, () => {
  hiddenKeys.value = hiddenColumnsFor(props.definition)
  handlePanel()
})
</script>

<template>
  <section class="table-page" :class="{ 'table-page--embedded': embedded }">
    <header v-if="!embedded" class="page-header">
      <div class="page-header__text">
        <div class="page-header__title-row">
          <h1 class="page-title">{{ definition.title }}</h1>
          <span class="prototype-pill">原型数据</span>
        </div>
        <p class="page-subtitle">{{ definition.purpose }}</p>
      </div>
      <div class="page-header__actions">
        <button v-if="canCreate" v-permission="definition.createPermission || undefined" type="button" class="btn btn--primary" @click="openCreate">
          <AppIcon name="plus" :size="15" />{{ definition.createLabel }}
        </button>
        <button v-if="canImport" v-permission="definition.importPermission || undefined" type="button" class="btn" @click="importOpen = true">
          <AppIcon name="import" :size="14" />批量导入
        </button>
        <button v-if="canExport" type="button" class="btn" @click="exportDefault">
          <AppIcon name="export" :size="14" />{{ exportScopeLabel }}
        </button>
        <button
          v-for="item in directExtras"
          :key="item.key"
          type="button"
          class="btn"
          @click="handleExtra(item)"
        ><AppIcon :name="item.icon || 'external'" :size="14" />{{ item.label }}</button>
        <el-dropdown v-if="moreExtras.length" trigger="click" @command="handleMoreExtra">
          <button type="button" class="btn btn--sm" aria-label="更多页面操作"><AppIcon name="more-horizontal" :size="16" /></button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item v-for="item in moreExtras" :key="item.key" :command="item.key">{{ item.label }}</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </header>

    <ProSummary v-if="!embedded && definition.summary?.length" :definition="definition" :summary="table.summary" />

    <slot name="before-filters" />

    <ProFilters
      :model-value="table.filters"
      @update:model-value="applyFilters"
      :definition="definition"
      :keyword="table.keyword.value"
      :active-count="table.activeFilterCount.value"
      @update:keyword="table.keyword.value = $event"
      @reset="resetFilters"
      @search="table.reload"
    />

    <div class="table-card">
      <div class="table-card__head">
        <div class="table-card__title">{{ definition.title }}列表 <span class="table-card__count">（{{ table.total.value.toLocaleString('zh-CN') }} 条）</span></div>
        <div v-if="table.selectedKeys.value.length" class="toolbar__selected">
          已选 {{ table.selectedKeys.value.length }} 项
          <button type="button" class="text-button" @click="table.selectedKeys.value = []">取消</button>
        </div>
        <div class="table-card__tools">
          <button type="button" class="table-tool" aria-label="刷新列表" title="刷新列表" @click="table.refresh"><AppIcon name="refresh" :size="15" /></button>
          <button type="button" class="table-tool" aria-label="列表字段设置" title="列表字段设置" @click="columnOpen = true"><AppIcon name="columns" :size="15" /></button>
          <el-dropdown trigger="click" @command="handleTableMore">
            <button type="button" class="table-tool" aria-label="更多列表操作" title="更多列表操作"><AppIcon name="more-horizontal" :size="15" /></button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="refresh">刷新列表</el-dropdown-item>
                <el-dropdown-item command="columns">字段设置</el-dropdown-item>
                <el-dropdown-item command="reset" :disabled="!table.activeFilterCount.value">重置筛选</el-dropdown-item>
                <el-dropdown-item v-if="canExport" command="export" divided>{{ exportScopeLabel }}</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>

      <div v-if="table.selectedKeys.value.length && batchActions.length" class="batch-bar">
        <span>已选择 <b>{{ table.selectedKeys.value.length }}</b> 项</span>
        <button
          v-for="action in batchActions"
          :key="action.key"
          type="button"
          class="btn btn--sm"
          :class="action.tone === 'danger' ? 'btn--danger' : action.tone === 'primary' ? 'btn--soft' : ''"
          :disabled="batchBusy"
          @click="handleBatch(action)"
        >{{ batchBusy ? '处理中…' : action.label }}</button>
        <button type="button" class="text-button" @click="table.selectedKeys.value = []">取消选择</button>
      </div>

      <div v-if="table.loading.value" class="table-skeleton" aria-label="列表加载中" aria-busy="true">
        <div v-for="index in 6" :key="index" class="table-skeleton__row">
          <span class="sk sk--check"></span><span class="sk sk--avatar"></span><span class="sk sk--wide"></span><span class="sk"></span><span class="sk"></span><span class="sk sk--short"></span>
        </div>
      </div>

      <div v-else-if="table.status.value === 'forbidden'" class="empty-state" role="alert">
        <span class="empty-state__art" aria-hidden="true"><AppIcon name="lock" :size="44" /></span>
        <h3 class="empty-state__title">当前角色没有查看该列表的权限</h3>
        <p class="empty-state__text">需要权限 {{ definition.permission }}。登录状态仍然有效，可返回工作台或由超级管理员调整角色授权。</p>
        <div class="empty-state__actions">
          <button type="button" class="btn btn--primary" @click="router.push({ path: '/403', query: { from: route.fullPath, need: definition.permission } })"><AppIcon name="lock" :size="14" />查看权限说明</button>
          <button type="button" class="btn" @click="router.push('/dashboard')">返回工作台</button>
        </div>
      </div>

      <div v-else-if="table.status.value === 'error' || table.status.value === 'offline'" class="empty-state" role="alert">
        <span class="empty-state__art" aria-hidden="true"><AppIcon :name="table.status.value === 'offline' ? 'wifi-off' : 'warning'" :size="44" /></span>
        <h3 class="empty-state__title">{{ table.status.value === 'offline' ? '网络连接已断开' : '列表加载失败' }}</h3>
        <p class="empty-state__text">{{ table.error.value }}<template v-if="table.errorCode.value">（请求编号 {{ table.errorCode.value }}）</template></p>
        <div class="empty-state__actions">
          <button type="button" class="btn btn--primary" @click="table.refresh"><AppIcon name="refresh" :size="14" />重试</button>
          <button type="button" class="btn" @click="resetFilters">清空筛选</button>
        </div>
      </div>

      <div v-else-if="table.status.value === 'empty'" class="empty-state">
        <span class="empty-state__art" aria-hidden="true"><AppIcon name="records" :size="44" /></span>
        <h3 class="empty-state__title">{{ table.activeFilterCount.value ? '没有符合筛选条件的数据' : definition.emptyHint || '还没有业务数据' }}</h3>
        <p class="empty-state__text">{{ table.activeFilterCount.value ? '保留当前筛选条件，可清空筛选或调整业务范围后重新查询。' : '新增第一条记录后，列表、汇总和关联页面会同步更新。' }}</p>
        <div class="empty-state__actions">
          <button v-if="table.activeFilterCount.value" type="button" class="btn" @click="resetFilters">清空筛选</button>
          <button v-if="canCreate" type="button" class="btn btn--primary" @click="openCreate"><AppIcon name="plus" :size="14" />{{ definition.createLabel }}</button>
        </div>
      </div>

      <div v-else class="table-scroll" :class="{ 'is-refreshing': table.refreshing.value }">
        <table class="pro-table" :style="{ '--table-min': `${tableMinWidth}px` }">
          <caption>{{ definition.title }}列表，共 {{ table.total.value.toLocaleString('zh-CN') }} 条</caption>
          <thead>
            <tr>
              <th v-if="selectable" class="is-check">
                <el-checkbox :model-value="allSelected" :indeterminate="someSelected" aria-label="全选当前页" @change="toggleAll(Boolean($event))" />
              </th>
              <th
                v-for="column in visibleColumns"
                :key="column.key"
                :class="{ 'is-sortable': column.sortable, 'is-num': column.align === 'right' }"
                :style="column.width ? { width: `${column.width}px` } : { minWidth: `${column.minWidth ?? 110}px` }"
                :tabindex="column.sortable ? 0 : undefined"
                :aria-label="column.sortable ? `按${column.label}排序` : undefined"
                :aria-sort="column.sortable ? (table.sortField.value === column.key ? (table.sortOrder.value === 'asc' ? 'ascending' : 'descending') : 'none') : undefined"
                @click="column.sortable && table.sortBy(column.key)"
                @keydown.enter.prevent="column.sortable && table.sortBy(column.key)"
                @keydown.space.prevent="column.sortable && table.sortBy(column.key)"
              >
                {{ column.label }}
                <span v-if="column.sortable" class="sort-mark" aria-hidden="true">{{ table.sortField.value === column.key ? (table.sortOrder.value === 'asc' ? '↑' : '↓') : '↕' }}</span>
              </th>
              <th class="is-actions">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in table.rows.value" :key="String(row.id)" :class="{ 'is-selected': table.selectedKeys.value.includes(String(row.id)) }">
              <td v-if="selectable" class="is-check">
                <el-checkbox
                  :model-value="table.selectedKeys.value.includes(String(row.id))"
                  :aria-label="`选择 ${primaryName(row)}`"
                  @change="toggleRow(row, Boolean($event))"
                />
              </td>
              <td
                v-for="column in visibleColumns"
                :key="column.key"
                :class="{
                  'is-num': column.align === 'right' || ['number', 'money', 'percent'].includes(column.type ?? ''),
                  'cell-danger': column.type === 'status' && statusToneFor(row, column.key, column.options) === 'danger',
                  'is-datetime': column.type === 'datetime' || column.type === 'date',
                }"
              >
                <template v-if="column.type === 'primary'">
                  <button type="button" class="cell-main cell-main--button" @click="openDetail(row)">
                    <span class="avatar avatar--sm" :class="avatarClass(row)">{{ column.key === 'generic_name' ? String(primaryName(row)).slice(0, 1) : avatarText(primaryName(row)) }}</span>
                    <span class="cell-main__text">
                      <span class="cell-main__title">{{ primaryName(row) }}</span>
                      <span v-if="subtitle(row)" class="cell-main__sub">{{ subtitle(row) }}</span>
                    </span>
                  </button>
                </template>
                <template v-else-if="column.key === 'user_name' || column.type === 'user'">
                  <button type="button" class="cell-main cell-main--button" @click="openDetail(row)">
                    <span class="avatar avatar--xs" :class="avatarClass(row)">{{ avatarText(row.user_name) }}</span>
                    <span class="cell-main__text">
                      <span class="cell-main__title">{{ row.user_name || '—' }}</span>
                      <span v-if="column.subtitleKey && row[column.subtitleKey]" class="cell-main__sub">{{ row[column.subtitleKey] }}</span>
                    </span>
                  </button>
                </template>
                <template v-else-if="column.type === 'status'">
                  <span class="tag" :class="`tag--${statusToneFor(row, column.key, column.options)}`">{{ statusLabel(row, column.key, column.options) }}</span>
                </template>
                <template v-else-if="column.type === 'boolean'">
                  <span class="boolean-state" :class="{ 'is-true': Boolean(row[column.key]) }">
                    <i aria-hidden="true"></i>{{ Boolean(row[column.key]) ? '是' : '否' }}
                  </span>
                </template>
                <template v-else-if="column.type === 'tag'">
                  <span v-if="Array.isArray(row[column.key]) && (row[column.key] as unknown[]).length" class="tag-row">
                    <span v-for="item in (row[column.key] as unknown[]).slice(0, 3)" :key="String(item)" class="tag tag--neutral tag--plain">{{ String(item) }}</span>
                    <span v-if="(row[column.key] as unknown[]).length > 3" class="tag tag--neutral tag--plain">+{{ (row[column.key] as unknown[]).length - 3 }}</span>
                  </span>
                  <span v-else-if="row[column.key]" class="tag tag--info tag--plain">{{ displayValue(row, column) }}</span>
                  <span v-else class="cell-muted">—</span>
                </template>
                <template v-else-if="column.type === 'progress'">
                  <span class="progress-cell">
                    <span class="progress-track"><i :style="{ width: `${progressValue(row, column)}%` }" :class="`progress-track--${cellTone(row, column, definition)}`"></i></span>
                    <em>{{ formatNumber(row[column.key]) }}%</em>
                  </span>
                </template>
                <template v-else-if="column.key === 'stock'">
                  <span class="stock-cell">
                    <b>{{ displayValue(row, column) }}</b>
                    <span class="tag" :class="`tag--${Number(row.stock) <= 0 ? 'danger' : Number(row.stock) < Number(row.safety_stock) ? 'warning' : 'success'}`">
                      {{ Number(row.stock) <= 0 ? '已缺货' : Number(row.stock) < Number(row.safety_stock) ? '库存低' : '库存充足' }}
                    </span>
                  </span>
                </template>
                <template v-else-if="column.type === 'money'">
                  {{ formatMoney(row[column.key]) }}
                </template>
                <template v-else-if="column.type === 'datetime'">
                  {{ formatDateTime(row[column.key]) }}
                </template>
                <template v-else-if="column.type === 'date'">
                  {{ formatCell(row, column.key) }}
                </template>
                <template v-else-if="column.type === 'percent'">
                  {{ formatPercent(row[column.key]) }}
                </template>
                <template v-else-if="column.type === 'image'">
                  <span class="asset-thumb" :style="{ background: String(row.cover_color ?? '#DDF3EA') }">{{ String(row.name ?? '素材').slice(0, 1) }}</span>
                </template>
                <template v-else-if="column.key === 'status'">
                  <ProStatus :value="row[column.key] as string" :options="column.options" :label="statusLabel(row, column.key, column.options)" />
                </template>
                <template v-else>
                  <span :class="{ 'cell-clamp': ['content', 'indication', 'answer', 'summary', 'description'].includes(column.key), 'cell-muted': !row[column.key] }">{{ displayValue(row, column) }}</span>
                </template>
              </td>
              <td class="is-actions">
                <div class="row-actions">
                  <template v-for="(action, index) in preferredActions(row)" :key="`${action.key}-${index}`">
                    <button
                      type="button"
                      class="row-action"
                      :class="{ 'row-action--danger': action.tone === 'danger', 'row-action--muted': action.tone === 'default' }"
                      @click="handleRowAction(action, row)"
                    >{{ action.label }}</button>
                    <span v-if="index < preferredActions(row).length - 1" class="row-action-sep" aria-hidden="true"></span>
                  </template>
                  <el-dropdown v-if="hasMoreForRow(row)" trigger="click" @command="handleMoreCommand($event, row)">
                    <button type="button" class="more-button" :aria-label="`更多操作：${primaryName(row)}`"><AppIcon name="more-horizontal" :size="16" /></button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <el-dropdown-item v-for="(action, index) in moreRowActions(row)" :key="`${action.key}-${index}`" :command="action">
                          <span :class="{ 'danger-text': action.tone === 'danger' }">{{ action.label }}</span>
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                  <span v-if="!availableRowActions(row).length" class="cell-muted">仅查看</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <ProPagination
        v-if="table.status.value !== 'loading' && table.status.value !== 'error' && table.status.value !== 'forbidden' && table.status.value !== 'empty'"
        :page="table.page.value"
        :page-size="table.pageSize.value"
        :total="table.total.value"
        @update:page="table.setPage"
        @update:page-size="table.setPageSize"
      />
    </div>

    <slot name="after-table" />

    <ProDetailDrawer
      :open="detailOpen"
      :definition="definition"
      :record="detailRow"
      :can-edit="canEdit"
      @close="detailOpen = false"
      @edit="openEdit"
    />

    <ProFormDrawer
      :open="formOpen"
      :mode="formMode"
      :definition="definition"
      :record="formRow"
      :preset="formPreset"
      @close="formOpen = false"
      @saved="onSaved"
    />

    <ActionDialog
      :open="actionOpen"
      :title="actionTitle"
      :description="actionDescription"
      :fields="actionFields"
      :confirm-text="actionConfirmText"
      :tone="actionTone"
      :loading="actionLoading"
      :definition="definition"
      @close="closeAction"
      @confirm="confirmAction"
    />

    <ColumnSettingsDialog
      :open="columnOpen"
      :definition="definition"
      :hidden-keys="hiddenKeys"
      @close="columnOpen = false"
      @apply="updateColumnSettings"
    />

    <ImportDialog
      :open="importOpen"
      :definition="definition"
      @close="importOpen = false"
      @import="handleImport"
    />

    <MasterDataDrawer
      :open="masterOpen"
      :config="masterConfig"
      @close="masterOpen = false"
      @changed="table.reload"
    />

    <RolePermissionsDialog
      :open="roleDialogOpen"
      :record="roleDialogRecord"
      @close="roleDialogOpen = false"
      @saved="table.reload"
    />
  </section>
</template>

<style scoped>
.table-page { min-width: 0; }
.table-page--embedded .pro-filterbar { margin-top: 0; }
.table-card { position: relative; }
.table-tool { width: 28px; height: 28px; border: 0; border-radius: 6px; display: grid; place-items: center; background: transparent; color: var(--c-muted); }
.table-tool:hover { background: var(--c-brand-mist); color: var(--c-brand-strong); }
.cell-main--button { border: 0; background: transparent; padding: 0; text-align: left; max-width: 100%; }
.cell-main--button:hover .cell-main__title { color: var(--c-brand-strong); }
.boolean-state { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; color: var(--c-muted); }
.boolean-state i { width: 6px; height: 6px; border-radius: 50%; background: #c7d3df; }
.boolean-state.is-true { color: var(--c-brand-strong); }
.boolean-state.is-true i { background: var(--c-brand); }
.progress-cell { display: flex; align-items: center; gap: 6px; min-width: 74px; }
.progress-track { flex: 1; height: 5px; border-radius: 3px; background: #edf3f7; overflow: hidden; }
.progress-track i { display: block; height: 100%; border-radius: 3px; background: var(--c-brand); }
.progress-track--warning i { background: var(--c-warning); }
.progress-track--danger i { background: var(--c-danger); }
.progress-cell em { font-style: normal; font-size: 10px; color: var(--c-muted); min-width: 30px; text-align: right; }
.stock-cell { display: flex; align-items: center; gap: 6px; white-space: nowrap; }
.stock-cell b { font-size: 11px; color: var(--c-text); font-weight: 600; }
.asset-thumb { width: 26px; height: 26px; border-radius: 6px; display: grid; place-items: center; color: #4b7a68; font-size: 11px; font-weight: 700; }
.batch-bar { display: flex; align-items: center; gap: 7px; flex-wrap: wrap; padding: 8px 16px; background: #f4fbf8; border-bottom: 1px solid var(--c-success-border); font-size: 11px; color: var(--c-brand-strong); }
.batch-bar b { font-family: var(--font-num); }
.batch-bar .btn { height: 26px; }
.batch-bar .text-button { margin-left: auto; }
.text-button { border: 0; background: transparent; color: var(--c-muted); font-size: 10.5px; padding: 2px 4px; }
.text-button:hover { color: var(--c-danger); }
.is-refreshing { opacity: .64; pointer-events: none; }
.table-skeleton { padding: 0 16px; }
.table-skeleton__row { display: flex; align-items: center; gap: 14px; height: var(--row-h); border-bottom: 1px solid var(--c-divider); }
.sk { height: 9px; border-radius: 5px; background: linear-gradient(90deg, #f2f5f8 20%, #e9eff4 40%, #f2f5f8 60%); background-size: 200% 100%; animation: shimmer 1.25s linear infinite; width: 64px; }
.sk--check { width: 14px; height: 14px; border-radius: 4px; }
.sk--avatar { width: 28px; height: 28px; border-radius: 50%; }
.sk--wide { width: 180px; }
.sk--short { width: 80px; }
@keyframes shimmer { to { background-position: -200% 0; } }
.empty-state__art { color: #b7c7d6; }
</style>
